<template>
  <div class="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-10 px-4">
    <div class="w-full max-w-5xl bg-white rounded-2xl shadow-md p-8">
      <!-- Page Title -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-semibold mb-2">Create Your School's Account</h1>
        <p class="text-gray-600">Welcome! Please fill out the form below to register your school.</p>
      </div>

      <!-- School Details -->
      <section class="mb-8">
        <h2 class="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">
          School Details
        </h2>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">School Name</label>
            <InputText v-model="schoolDetails.schoolName" placeholder="Enter School Name" class="w-full"
              :class="{ 'p-invalid': v$.schoolDetails.schoolName.$error || getErrorMessage('schoolName') }" />
            <small v-if="v$.schoolDetails.schoolName.$error" class="text-red-600">School name is required.</small>
            <small v-if="getErrorMessage('schoolName')" class="text-red-600">{{ getErrorMessage('schoolName') }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">School Address</label>
            <InputText v-model="schoolDetails.schoolAddress" placeholder="Enter School Address" class="w-full"
              :class="{ 'p-invalid': v$.schoolDetails.schoolAddress.$error || getErrorMessage('schoolAddress') }" />
            <small v-if="v$.schoolDetails.schoolAddress.$error" class="text-red-600">School address is required.</small>
            <small v-if="getErrorMessage('schoolAddress')" class="text-red-600">{{ getErrorMessage('schoolAddress')
              }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">School Email</label>
            <InputText v-model="schoolDetails.schoolEmail" placeholder="Enter School Email" class="w-full"
              :class="{ 'p-invalid': v$.schoolDetails.schoolEmail.$error || getErrorMessage('schoolEmail') }" />
            <small v-if="v$.schoolDetails.schoolEmail.$error" class="text-red-600">
              <span v-if="v$.schoolDetails.schoolEmail.required.$invalid">Email is required.</span>
              <span v-else-if="v$.schoolDetails.schoolEmail.email.$invalid">Enter a valid email address.</span>
            </small>
            <small v-if="getErrorMessage('schoolEmail')" class="text-red-600">{{ getErrorMessage('schoolEmail')
              }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">School Mobile</label>
            <InputText v-model="schoolDetails.schoolMobile" placeholder="Enter School Mobile Number" class="w-full"
              :class="{ 'p-invalid': v$.schoolDetails.schoolMobile.$error || getErrorMessage('schoolMobile') }" />
            <small v-if="v$.schoolDetails.schoolMobile.$error" class="text-red-600">Mobile number is required.</small>
            <small v-if="getErrorMessage('schoolMobile')" class="text-red-600">{{ getErrorMessage('schoolMobile')
              }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <InputText v-model="schoolDetails.website" placeholder="Enter School Website" class="w-full"
              :class="{ 'p-invalid': v$.schoolDetails.website.$error || getErrorMessage('website') }" />
            <small v-if="v$.schoolDetails.website.$error" class="text-red-600">Website is required.</small>
            <small v-if="getErrorMessage('website')" class="text-red-600">{{ getErrorMessage('website') }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">School Type</label>
            <Select v-model="schoolDetails.school_type" :options="schoolTypes" optionLabel="label" optionValue="value"
              placeholder="Select School Type" class="w-full"
              :class="{ 'p-invalid': v$.schoolDetails.school_type.$error || getErrorMessage('school_type') }" />
            <small v-if="v$.schoolDetails.school_type.$error" class="text-red-600">School type is required.</small>
            <small v-if="getErrorMessage('school_type')" class="text-red-600">{{ getErrorMessage('school_type')
              }}</small>
          </div>
        </div>
      </section>

      <!-- Administrator Account -->
      <section>
        <h2 class="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">
          Your Administrator Account
        </h2>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <InputText v-model="userDetails.name" placeholder="Enter Full Name" class="w-full"
              :class="{ 'p-invalid': v$.userDetails.name.$error || getErrorMessage('name') }" />
            <small v-if="v$.userDetails.name.$error" class="text-red-600">Name is required.</small>
            <small v-if="getErrorMessage('name')" class="text-red-600">{{ getErrorMessage('name') }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <InputText v-model="userDetails.email" placeholder="Enter Email Address" class="w-full"
              :class="{ 'p-invalid': v$.userDetails.email.$error || getErrorMessage('email') }" />
            <small v-if="v$.userDetails.email.$error" class="text-red-600">
              <span v-if="v$.userDetails.email.required.$invalid">Email is required.</span>
              <span v-else-if="v$.userDetails.email.email.$invalid">Enter a valid email address.</span>
            </small>
            <small v-if="getErrorMessage('email')" class="text-red-600">{{ getErrorMessage('email') }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <Password v-model="userDetails.password" placeholder="Enter Password" toggleMask class="w-full"
              inputClass="w-full" :feedback="false"
              :class="{ 'p-invalid': v$.userDetails.password.$error || getErrorMessage('password') }" />
            <small v-if="v$.userDetails.password.$error" class="text-red-600">
              <span v-if="v$.userDetails.password.required.$invalid">Password is required.</span>
            </small>
            <small v-if="getErrorMessage('password')" class="text-red-600">{{ getErrorMessage('password') }}</small>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <Password v-model="userDetails.password_confirmation" placeholder="Confirm Password" toggleMask
              class="w-full" inputClass="w-full" :feedback="false"
              :class="{ 'p-invalid': v$.userDetails.password_confirmation.$error || getErrorMessage('password_confirmation') }" />
            <small v-if="v$.userDetails.password_confirmation.$error" class="text-red-600">
              <span v-if="v$.userDetails.password_confirmation.required.$invalid">Confirm password is required.</span>
              <span v-else-if="v$.userDetails.password_confirmation.sameAsPassword.$invalid">
                Passwords do not match.
              </span>
            </small>
            <small v-if="getErrorMessage('password_confirmation')" class="text-red-600">{{
              getErrorMessage('password_confirmation') }}</small>
          </div>
        </div>
      </section>

      <!-- Submit Button -->
      <div class="mt-8 flex justify-end">
        <Button label="Create Account" icon="pi pi-check"
          class="bg-primary hover:bg-primary/90 border-none text-white px-5 py-2 rounded-md shadow-md transition"
          :loading="isSubmitting" @click="handleSubmit" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, email, sameAs } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { useUserStore } from '@/store/userStore';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { useFormErrors } from '@/composables/useFormErrors';
import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';

const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const toast = useToast();
const userStore = useUserStore();
const { createSchool } = useSchoolMutations();
const isSubmitting = ref(false);

const schoolDetails = ref({
  schoolName: '',
  schoolAddress: '',
  schoolEmail: '',
  schoolMobile: '',
  website: '',
  school_type: '',
});

const userDetails = ref({
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
});

const schoolTypes = [
  { label: 'Primary', value: 'primary' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Tertiary', value: 'tertiary' },
];

// ✅ Validation Rules
const rules = computed(() => ({
  schoolDetails: {
    schoolName: { required },
    schoolAddress: { required },
    schoolEmail: { required, email },
    schoolMobile: { required },
    website: { required },
    school_type: { required },
  },
  userDetails: {
    name: { required },
    email: { required, email },
    password: { required },
    password_confirmation: {
      required,
      sameAsPassword: sameAs(userDetails.value.password),
    },
  },
}));

const v$ = useVuelidate(rules, { schoolDetails, userDetails });

const handleSubmit = async() => {
  clearErrors();
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;
  try {
    const { school_type } = schoolDetails.value;
    await createSchool(schoolDetails.value, userDetails.value);

    if (school_type) localStorage.setItem(LocalStorageKeys.schoolType, school_type);

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'School account created successfully!',
      life: 3000,
    });

    Object.keys(schoolDetails.value).forEach((key) => {
      (schoolDetails.value as Record<string, string>)[key] = '';
    });
    Object.keys(userDetails.value).forEach((key) => {
      (userDetails.value as Record<string, string>)[key] = '';
    });

    v$.value.$reset();
  } catch (error) {
    handleServerErrors(error);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'An error occurred while creating the school. Please try again.',
        life: 3000,
      });
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.p-inputtext:focus {
  border-color: #047857 !important;
  box-shadow: 0 0 0 1px #047857 !important;
}
</style>
